/**
 * Room × direction table for the Vastu Direction Checker.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS DOCTRINE, NOT COMPUTATION.
 *
 * Nothing here is derived or calculated. Every cell is a traditional Vastu
 * Shastra position, and "accuracy" means faithfulness to that tradition plus
 * sign-off from a practitioner — not a numerical tolerance. No engineer should
 * invent or adjust a cell.
 *
 * PENDING: Deepa Saxena to review all 56 cells before this tool is indexed.
 * `vastu.test.ts` snapshots the table, so any later edit fails CI and forces a
 * fresh review in the pull request.
 *
 * The reasoning follows the mainstream convention: each of the eight directions
 * is governed by an element and a presiding deity, and a room is well placed
 * when its function agrees with that element. Verdicts are deliberately only
 * three-valued, and the harshest is "needs remedy" — never a warning of harm.
 * Remedies are all non-structural, because almost nobody can move a room.
 *
 * Content here must stay consistent with the Vastu articles in content/ — the
 * site should never contradict itself between a blog post and a tool.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const DIRECTIONS = [
  'North', 'North-East', 'East', 'South-East',
  'South', 'South-West', 'West', 'North-West',
] as const;

export const ROOMS = [
  'Main Door', 'Kitchen', 'Bedroom', 'Pooja Room', 'Bathroom', 'Study', 'Living Room',
] as const;

export type Direction = (typeof DIRECTIONS)[number];
export type Room = (typeof ROOMS)[number];

export type Verdict = 'auspicious' | 'acceptable' | 'needs-remedy';

export const VERDICT_LABELS: Record<Verdict, string> = {
  auspicious: 'Auspicious',
  acceptable: 'Acceptable',
  'needs-remedy': 'Needs Remedy',
};

/** Element and presiding deity for each direction — shown as the reasoning. */
export const DIRECTION_ELEMENTS: Record<Direction, { element: string; lord: string }> = {
  North: { element: 'Water', lord: 'Kubera' },
  'North-East': { element: 'Water / Space', lord: 'Ishanya' },
  East: { element: 'Air', lord: 'Indra' },
  'South-East': { element: 'Fire', lord: 'Agni' },
  South: { element: 'Earth / Fire', lord: 'Yama' },
  'South-West': { element: 'Earth', lord: 'Nairitya' },
  West: { element: 'Space', lord: 'Varuna' },
  'North-West': { element: 'Air', lord: 'Vayu' },
};

export interface Cell {
  verdict: Verdict;
  /** Why — shown to the user. Reasoning, not just a verdict. */
  principle: string;
  /** Three to four non-structural, actionable steps. */
  remedies: string[];
}

const T: Record<Room, Record<Direction, Cell>> = {
  'Main Door': {
    North: {
      verdict: 'auspicious',
      principle: 'North is the quarter of Kubera and the water element, traditionally linked to flow, opportunity and wealth.',
      remedies: [
        'Keep the entrance well lit and clear of shoe racks so the door opens a full 90 degrees.',
        'Use a clean doormat and refresh the paintwork — a tired door undoes a good direction.',
        'Avoid placing a mirror directly opposite the door.',
      ],
    },
    'North-East': {
      verdict: 'auspicious',
      principle: 'The North-East (Ishanya) is the most sacred quarter, associated with clarity and calm entry of energy.',
      remedies: [
        'Keep this entrance especially clean and uncluttered.',
        'Favour light, natural finishes over heavy dark ones.',
        'Keep the area free of storage, dustbins and heavy furniture.',
      ],
    },
    East: {
      verdict: 'auspicious',
      principle: 'East is governed by Indra and the rising sun, traditionally linked to vitality and social standing.',
      remedies: [
        'Let in as much morning light as the entrance allows.',
        'Keep the threshold clean and the hinges oiled.',
        'A brass nameplate on the right of the door is the traditional touch.',
      ],
    },
    'South-East': {
      verdict: 'needs-remedy',
      principle: 'The South-East belongs to Agni. An entrance here brings fire energy to a threshold, which tradition links to friction and disputes.',
      remedies: [
        'Glue a 3mm thick copper strip along the floor beneath the closed door.',
        'Keep the entrance lighting warm rather than harsh, and avoid red at the threshold.',
        'Keep the doorway clear and quiet — no shoe piles, no clutter.',
      ],
    },
    South: {
      verdict: 'acceptable',
      principle: 'South is ruled by Yama and Mars. Not universally unfavourable: it traditionally suits people in law, real estate, surgery and the services, and depends on the resident’s chart.',
      remedies: [
        'Affix a 2-inch wide red vinyl tape or a 5mm wooden plank at the base of the entrance.',
        'Keep the door in good repair with a solid, well-finished surface.',
        'Have the exact compass degree checked — the pada matters more than the cardinal direction here.',
      ],
    },
    'South-West': {
      verdict: 'needs-remedy',
      principle: 'The South-West is the grounding, stabilising quarter. An opening here is traditionally read as leaking that stability.',
      remedies: [
        'Paste 3-inch wide yellow adhesive tape across the full width of the doorframe, or fit a 3mm brass strip beneath the door.',
        'Keep this door closed when not in use, and prefer another entrance for daily coming and going.',
        'Keep the surrounding zone heavy and settled — solid furniture, no voids.',
      ],
    },
    West: {
      verdict: 'acceptable',
      principle: 'West is Varuna’s quarter, associated with gains and stability. A sound secondary choice after North and East.',
      remedies: [
        'Fit a 3mm brass or iron strip at the threshold if the pada falls unfavourably.',
        'Keep the entrance well lit after dark.',
        'Avoid heavy overhead storage directly above the door.',
      ],
    },
    'North-West': {
      verdict: 'acceptable',
      principle: 'The North-West is Vayu’s quarter of air and movement, traditionally linked to travel and change rather than settledness.',
      remedies: [
        'Use a 3mm brass strip, or paint a 2-inch white line on the floor beneath the door.',
        'Keep the entrance uncluttered so movement through it stays easy.',
        'Good for a home where frequent travel is normal; less so where you want to put down roots.',
      ],
    },
  },

  Kitchen: {
    'South-East': {
      verdict: 'auspicious',
      principle: 'The South-East is the quarter of Agni, the fire element — the natural home of a kitchen.',
      remedies: [
        'Use red, brown or green countertop surfaces rather than black.',
        'Face East while cooking where the layout allows.',
        'Keep the stove and sink at least three feet apart.',
      ],
    },
    'North-West': {
      verdict: 'auspicious',
      principle: 'The North-West is the air quarter, and air sustains fire — the second natural placement, usually needing no remedy.',
      remedies: [
        'White, off-white or light grey surfaces suit this zone.',
        'Keep the ventilation genuinely working.',
        'Store heavier provisions along the South and West walls.',
      ],
    },
    North: {
      verdict: 'acceptable',
      principle: 'North is the water quarter. Fire sits uneasily here, though less sharply than in the North-East.',
      remedies: [
        'Move the stove as close to the South-East corner of the kitchen as the layout allows.',
        'Keep the sink and water filter to the North or North-East of the room.',
        'Avoid black or deep blue surfaces near the hob.',
      ],
    },
    'North-East': {
      verdict: 'needs-remedy',
      principle: 'The North-East is the water and sacred quarter. An active fire source here is a direct elemental clash.',
      remedies: [
        'Place a thick green marble or green Baroda stone slab directly beneath the gas stove as an earth barrier.',
        'Move the dustbin out of the North-East corner.',
        'Keep the zone light, clean and free of heavy appliances.',
        'Relocate the refrigerator to the North-West or South-West.',
      ],
    },
    East: {
      verdict: 'acceptable',
      principle: 'East carries the morning sun and supports the cooking function reasonably well, without the natural fit of the South-East.',
      remedies: [
        'Position the hob toward the South-East corner of the room.',
        'Face East while cooking.',
        'Keep the East wall clear of heavy overhead cabinets.',
      ],
    },
    South: {
      verdict: 'acceptable',
      principle: 'South carries fire energy and tolerates a kitchen, though the South-East remains the preferred corner.',
      remedies: [
        'Shift the hob toward the South-East of the room where possible.',
        'Keep heavy storage on the South and West walls.',
        'Warm earth tones suit this placement.',
      ],
    },
    'South-West': {
      verdict: 'needs-remedy',
      principle: 'The South-West is the earth quarter and the anchor of the home. Fire here is traditionally read as destabilising it.',
      remedies: [
        'Place a yellow Jaisalmer stone slab directly beneath the cooking hob.',
        'Or paint the backsplash behind the stove a solid yellow.',
        'Keep the corner itself heavy and settled rather than open.',
      ],
    },
    West: {
      verdict: 'acceptable',
      principle: 'West is a workable secondary placement for a kitchen, without the elemental support of the South-East.',
      remedies: [
        'Bring the hob toward the South-East of the room.',
        'Keep the sink clear of the immediate stove area.',
        'Neutral or earthy surfaces suit this zone.',
      ],
    },
  },

  Bedroom: {
    'South-West': {
      verdict: 'auspicious',
      principle: 'The South-West is the heaviest, most grounded quarter — traditionally the master bedroom, for stability and deep rest.',
      remedies: [
        'Sleep with your head pointing South.',
        'Keep the heaviest furniture along the South and West walls.',
        'Avoid mirrors facing the bed.',
      ],
    },
    South: {
      verdict: 'auspicious',
      principle: 'South supports rest and recovery, and suits a head-South sleeping position naturally.',
      remedies: [
        'Head to the South while sleeping.',
        'Keep the room free of water features.',
        'Favour warm, muted tones over stimulating colours.',
      ],
    },
    West: {
      verdict: 'acceptable',
      principle: 'West is a settled quarter and works well for bedrooms, particularly for children who have outgrown the East.',
      remedies: [
        'Head to the South or East while sleeping.',
        'Keep wardrobes along the South or West wall.',
        'Avoid heavy clutter under the bed.',
      ],
    },
    'North-West': {
      verdict: 'acceptable',
      principle: 'The North-West is the quarter of movement, traditionally suited to guest rooms or to someone about to move on.',
      remedies: [
        'Good for a guest room or a young adult’s room.',
        'Head to the South or East while sleeping.',
        'Keep the room tidy — movement energy plus clutter is restless.',
      ],
    },
    North: {
      verdict: 'acceptable',
      principle: 'North is Kubera’s quarter and works for a bedroom, though it is lighter energy than the South-West anchor.',
      remedies: [
        'Head to the South or East while sleeping — never to the North.',
        'Keep the North wall relatively light and uncluttered.',
        'Suits a study-bedroom combination well.',
      ],
    },
    East: {
      verdict: 'acceptable',
      principle: 'East carries morning energy, traditionally suiting children and students rather than a master bedroom.',
      remedies: [
        'Head to the East while sleeping, for focus and study.',
        'Let in the morning light.',
        'Keep heavy storage to the South and West of the room.',
      ],
    },
    'North-East': {
      verdict: 'needs-remedy',
      principle: 'The North-East is the sacred quarter, traditionally reserved for prayer and meditation rather than sleep.',
      remedies: [
        'If it must be a bedroom, keep it exceptionally light, clean and sparse.',
        'Never sleep with the head to the North here.',
        'Avoid heavy wardrobes and storage in this zone.',
        'Better used as a pooja room, study or meditation space if you have the option.',
      ],
    },
    'South-East': {
      verdict: 'needs-remedy',
      principle: 'The South-East is Agni’s fire quarter. Sleeping in it is traditionally linked to restlessness and short temper.',
      remedies: [
        'Sleep with your head pointing South.',
        'Avoid red and strong orange in the decor; favour cool, muted tones.',
        'Keep electronics and heat sources out of the room.',
        'Better suited to a couple’s room than a child’s.',
      ],
    },
  },

  'Pooja Room': {
    'North-East': {
      verdict: 'auspicious',
      principle: 'The North-East (Ishanya) is the most sacred quarter and the traditional place for worship.',
      remedies: [
        'Face East or North while praying.',
        'Keep the space scrupulously clean and free of storage.',
        'Use light, natural materials — marble, brass, pale wood.',
      ],
    },
    East: {
      verdict: 'auspicious',
      principle: 'East, the quarter of the rising sun, is the second traditional choice for a shrine.',
      remedies: [
        'Face East while praying.',
        'Keep the shrine raised off the floor.',
        'Let in natural morning light where you can.',
      ],
    },
    North: {
      verdict: 'acceptable',
      principle: 'North is a workable placement for worship, sharing the light, upward quality of the North-East.',
      remedies: [
        'Face East or North while praying.',
        'Keep the surrounding wall light in colour.',
        'Avoid placing the shrine beneath a staircase.',
      ],
    },
    West: {
      verdict: 'acceptable',
      principle: 'West is tolerable for a shrine when the North-East is unavailable, though it lacks the traditional support.',
      remedies: [
        'Position the deities so that you face East while praying.',
        'Keep the immediate area clean and free of household storage.',
        'Raise the shrine off the floor.',
      ],
    },
    'North-West': {
      verdict: 'acceptable',
      principle: 'The North-West is a movement quarter — usable for a shrine, but not settled.',
      remedies: [
        'Face East while praying.',
        'Keep the space quiet and separate from thoroughfares.',
        'Avoid using the same alcove for storage.',
      ],
    },
    'South-East': {
      verdict: 'needs-remedy',
      principle: 'The fire quarter is traditionally the kitchen’s, and sits awkwardly with a shrine.',
      remedies: [
        'Face East or North while praying.',
        'Keep lamps and flames modest and well contained.',
        'Relocate to the North-East if the option exists.',
      ],
    },
    South: {
      verdict: 'needs-remedy',
      principle: 'South is Yama’s quarter and is traditionally avoided for worship.',
      remedies: [
        'Position the deities so that you face East or North while praying.',
        'Keep the shrine raised and clearly separated from the rest of the room.',
        'Relocate to the North-East or East if you can.',
      ],
    },
    'South-West': {
      verdict: 'needs-remedy',
      principle: 'The South-West is the heavy, grounding quarter, traditionally reserved for the master bedroom or storage rather than a shrine.',
      remedies: [
        'Face East or North while praying.',
        'Do not place the shrine directly on the floor.',
        'Relocate to the North-East where possible — this is the placement most worth changing.',
      ],
    },
  },

  Bathroom: {
    'North-West': {
      verdict: 'auspicious',
      principle: 'The North-West is the quarter of air and movement — the traditional placement for a bathroom, where waste moves out.',
      remedies: [
        'Keep it well ventilated and dry.',
        'Keep the door closed when not in use.',
        'Fix leaks promptly.',
      ],
    },
    West: {
      verdict: 'auspicious',
      principle: 'West is the second traditional placement for a bathroom and needs no particular correction.',
      remedies: [
        'Keep the space dry and well ventilated.',
        'Keep the door closed.',
        'Avoid it sharing a wall with the kitchen hob.',
      ],
    },
    South: {
      verdict: 'acceptable',
      principle: 'South is workable for a bathroom, particularly toward the South-West of the zone.',
      remedies: [
        'Keep drainage flowing toward the North or East.',
        'Keep the space dry and well lit.',
        'Use earthy rather than deep dark tones.',
      ],
    },
    'South-East': {
      verdict: 'acceptable',
      principle: 'A bathroom is tolerable in the fire quarter, though water and fire sit uneasily together.',
      remedies: [
        'Do not share a wall directly with the kitchen hob.',
        'Keep the room well ventilated.',
        'Fix any leak the day you notice it.',
      ],
    },
    North: {
      verdict: 'acceptable',
      principle: 'North is the water quarter and tolerates a bathroom, provided the North-East corner itself stays clear.',
      remedies: [
        'Keep the North-East corner of the room free and light.',
        'Ensure drainage runs North or East.',
        'Keep the door closed and the space dry.',
      ],
    },
    East: {
      verdict: 'acceptable',
      principle: 'East is usable for a bathroom when it sits away from the North-East corner.',
      remedies: [
        'Keep the fittings toward the West of the room.',
        'Ventilate well and keep surfaces dry.',
        'Keep the door closed when not in use.',
      ],
    },
    'North-East': {
      verdict: 'needs-remedy',
      principle: 'The North-East is the sacred quarter. A bathroom here is one of the placements tradition treats most seriously.',
      remedies: [
        'Keep the room immaculately clean and dry at all times — this matters more here than anywhere.',
        'Keep the door closed and place a sea-salt bowl inside, replaced weekly.',
        'Never allow a leak to persist.',
        'If a renovation is ever on the table, this is the room to move first.',
      ],
    },
    'South-West': {
      verdict: 'needs-remedy',
      principle: 'The South-West anchors the home. Draining water out of it is traditionally read as draining stability.',
      remedies: [
        'Keep the room dry, and fix leaks immediately.',
        'Keep the door closed and use heavier, earth-toned finishes.',
        'Avoid using this bathroom as the primary one where an alternative exists.',
      ],
    },
  },

  Study: {
    'North-East': {
      verdict: 'auspicious',
      principle: 'The North-East is associated with clarity of mind, making it the traditional first choice for study.',
      remedies: [
        'Face East or North while working.',
        'Keep the desk clear and the zone light.',
        'Keep heavy storage out of this corner.',
      ],
    },
    East: {
      verdict: 'auspicious',
      principle: 'East carries the rising sun and is traditionally linked to learning and fresh focus.',
      remedies: [
        'Face East while studying.',
        'Use natural light where possible.',
        'Keep bookshelves to the South or West wall.',
      ],
    },
    North: {
      verdict: 'auspicious',
      principle: 'North is Kubera’s quarter, traditionally associated with career growth and clear communication.',
      remedies: [
        'Face North or East while working.',
        'Keep the North wall relatively light and open.',
        'Avoid sitting with your back to the door.',
      ],
    },
    West: {
      verdict: 'acceptable',
      principle: 'West works for a study, particularly for focused or repetitive work.',
      remedies: [
        'Face East or North while working.',
        'Keep heavy shelving on the West and South walls.',
        'Ensure good task lighting.',
      ],
    },
    'North-West': {
      verdict: 'acceptable',
      principle: 'The North-West suits short bursts of work and correspondence rather than deep, sustained study.',
      remedies: [
        'Face East or North while working.',
        'Keep the desk clear to offset the restlessness of this quarter.',
        'Better for admin than for exam preparation.',
      ],
    },
    South: {
      verdict: 'acceptable',
      principle: 'South supports disciplined, effortful work, though it is heavier than the northern quarters.',
      remedies: [
        'Face North or East while working.',
        'Keep heavy furniture along the South wall.',
        'Keep the room well lit.',
      ],
    },
    'South-West': {
      verdict: 'needs-remedy',
      principle: 'The South-West is heavy and settled — good for rest, traditionally read as dulling for study.',
      remedies: [
        'Face North or East while working, whatever the room’s direction.',
        'Keep the desk out of the South-West corner of the room itself.',
        'Keep the lighting bright and the desk clear.',
      ],
    },
    'South-East': {
      verdict: 'needs-remedy',
      principle: 'The fire quarter is traditionally linked to restlessness, which works against sustained concentration.',
      remedies: [
        'Face North or East while working.',
        'Avoid red and strong orange in the decor.',
        'Keep heat-producing equipment out of the room.',
      ],
    },
  },

  'Living Room': {
    North: {
      verdict: 'auspicious',
      principle: 'North is open, light and welcoming — a natural quarter for the room where guests are received.',
      remedies: [
        'Keep the North and East sides of the room lighter and more open.',
        'Seat the head of the household facing North or East.',
        'Keep the centre of the room uncluttered.',
      ],
    },
    'North-East': {
      verdict: 'auspicious',
      principle: 'The North-East brings clarity and calm, and suits a room used for conversation.',
      remedies: [
        'Keep this corner light and free of heavy furniture.',
        'Favour low seating toward the North and East.',
        'Keep the space open and airy.',
      ],
    },
    East: {
      verdict: 'auspicious',
      principle: 'East carries morning light and social energy, well matched to a living room.',
      remedies: [
        'Keep the East side open and well lit.',
        'Place heavier furniture on the South and West walls.',
        'Seat guests facing East or North.',
      ],
    },
    'North-West': {
      verdict: 'acceptable',
      principle: 'The North-West suits a room where people come and go, which describes most living rooms well enough.',
      remedies: [
        'Keep the seating arrangement open and easy to move through.',
        'Keep heavier pieces to the South and West.',
        'Good placement for a guest-facing sitting room.',
      ],
    },
    West: {
      verdict: 'acceptable',
      principle: 'West is a settled quarter and works for a family sitting room.',
      remedies: [
        'Keep the North and East sides of the room lighter.',
        'Seat the head of the household facing North or East.',
        'Ensure good evening lighting.',
      ],
    },
    'South-East': {
      verdict: 'acceptable',
      principle: 'The fire quarter brings liveliness to a social room, provided it does not tip into agitation.',
      remedies: [
        'Keep the decor cool and muted rather than red-dominant.',
        'Place electronics toward the South-East and keep the North-East clear.',
        'Keep the room well ventilated.',
      ],
    },
    South: {
      verdict: 'acceptable',
      principle: 'South is heavier but workable for a living room, especially a family one.',
      remedies: [
        'Keep the heaviest furniture along the South and West walls.',
        'Seat the head of the household facing North or East.',
        'Keep the North-East corner of the room open and light.',
      ],
    },
    'South-West': {
      verdict: 'needs-remedy',
      principle: 'The South-West is the anchor quarter, traditionally reserved for the master bedroom rather than a social room.',
      remedies: [
        'Keep the heaviest furniture in the South-West corner so the zone stays weighted.',
        'Keep the North-East corner of the room open, light and clear.',
        'Seat the head of the household in the South-West, facing North or East.',
      ],
    },
  },
};

/** Look up a single room/direction cell. */
export function lookup(room: Room, direction: Direction): Cell {
  return T[room][direction];
}

export const VASTU_TABLE = T;

/** Convention disclosed in the UI alongside every result. */
export const VASTU_CONVENTION =
  'Based on the mainstream Vastu Shastra convention, in which each direction is governed by an element and a presiding deity, and a room is well placed when its function agrees with that element. Where practitioners differ, we say so rather than picking silently.';
